import React from "react";
import {
  Plus,
  TrendingUp,
  DollarSign,
  AlertCircle,
  Shield,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  bondsAPI,
  formatCompactCurrency,
  formatDate,
} from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { createResponseSelector } from "../../utils/apiHelpers";
import { toNumberSafe } from "../../utils/moneyUtils";

const IssuerDashboard = () => {
  const navigate = useNavigate();

  // Fetch summary via bonds list to align widgets with BondManagement
  const {
    data: summaryData,
    isPending: summaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ["issuer-dashboard-summary"],
    queryFn: () => bondsAPI.getBonds({ page: 1, limit: 1 }),
    select: (response) => {
      const root = response?.data?.data ?? response?.data ?? response;
      const summary = root?.summary || {};
      return summary;
    },
  });

  // Fetch recent bonds for display (separate from statistics)
  const { data: recentBondsData, isPending: recentBondsLoading } = useQuery({
    queryKey: ["recent-bonds-dashboard"],
    queryFn: () => bondsAPI.getBonds({ page: 1, limit: 5 }), // Only fetch 5 recent bonds for display
    select: createResponseSelector("bonds", []),
  });

  // Debug statistics
  console.log("Debug - Summary loading:", summaryLoading);
  console.log("Debug - Summary error:", summaryError);
  console.log("Debug - Summary data:", summaryData);
  console.log("Debug - Summary data type:", typeof summaryData);
  console.log(
    "Debug - Summary data keys:",
    summaryData ? Object.keys(summaryData) : "no keys"
  );

  // Log any authentication or API errors
  if (summaryError) {
    console.error("Debug - API Error details:", summaryError);
  }

  // Extract statistics from the backend API response
  const summary = summaryData || {};
  const activeBonds = toNumberSafe(summary.activeCount);
  const totalIssuance = toNumberSafe(summary.totalIssuance);

  // Debug extracted values
  console.log("Debug - Extracted values:");
  console.log("Debug - activeBonds:", activeBonds);
  console.log("Debug - totalIssuance:", totalIssuance);
  // Removed burn/transfer request cards from dashboard per new requirements.

  // Note: Next payment calculation removed since we're using single stats API
  // This functionality can be added back if needed in the statistics API

  const handleCreateBond = () => {
    navigate("/issuer/bonds/create");
  };

  // Show loading state
  if (summaryLoading || recentBondsLoading) {
    return <LoadingSpinner size="lg" text="Loading dashboard..." />;
  }

  // Helper function to get status badge styling
  const getStatusBadge = (status) => {
    const statusClasses = {
      Active: "bg-green-100 text-green-800",
      Listed: "bg-green-100 text-green-800",
      "Pending Review": "bg-yellow-100 text-yellow-800",
      Approved: "bg-blue-100 text-blue-800",
      Rejected: "bg-red-100 text-red-800",
      Draft: "bg-gray-100 text-gray-800",
      "Redemption in Process": "bg-orange-100 text-orange-800",
      "Burn in Process": "bg-red-100 text-red-800",
      Matured: "bg-purple-100 text-purple-800",
    };

    const statusIcons = {
      Active: "✅",
      Listed: "🏛️",
      "Pending Review": "⏳",
      Approved: "✅",
      Rejected: "❌",
      Draft: "📝",
      "Redemption in Process": "🔄",
      "Burn in Process": "🔥",
      Matured: "🏁",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          statusClasses[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        <span className="mr-1">{statusIcons[status]}</span>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-12">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Issuer Dashboard
            </h1>
            <p className="text-secondary-600 mt-1">
              Manage your bond issuance and track performance
            </p>
          </div>
          <button
            onClick={handleCreateBond}
            className="btn-primary flex items-center space-x-2 w-full sm:w-auto justify-center"
          >
            <Plus className="w-4 h-4" />
            <span>Create New Bond</span>
          </button>
        </div>

        {/* Key Metrics (burn/transfer request cards removed) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 lg:gap-6 mb-8">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-primary-100">
                  <TrendingUp className="w-6 h-6 text-primary-600" />
                </div>
                <div className="ml-4 min-w-0 flex-1">
                  <p className="text-sm font-medium text-secondary-600 truncate">
                    Active Bonds
                  </p>
                  <p className="text-2xl font-bold ">{activeBonds}</p>
                  <p className="text-xs text-success-600 mt-1">
                    Total number of active bonds
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 rounded-lg bg-success-100">
                  <DollarSign className="w-6 h-6 text-success-600" />
                </div>
                <div className="ml-4 min-w-0 flex-1">
                  <p className="text-sm font-medium text-secondary-600 truncate">
                    Total Issuance
                  </p>
                  <p className="text-2xl font-bold">
                    {totalIssuance > 0
                      ? formatCompactCurrency(totalIssuance)
                      : "RM 0"}
                  </p>
                  <p className="text-xs text-success-600 mt-1">
                    Summation of total issuance of bonds
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Bonds & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Bonds */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium">Recent Bonds</h2>
                  <button
                    onClick={() => navigate("/issuer/bonds")}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    View All
                  </button>
                </div>
              </div>
              <div className="card-body">
                {/* Recent bonds from API data */}
                <div className="space-y-4">
                  {recentBondsData && recentBondsData.length > 0 ? (
                    recentBondsData.map((bond) => (
                      <div
                        key={bond._id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/issuer/bonds/${bond._id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              {bond.bondName}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                              Type: {bond.productType === "Fixed Notes" ? "Conventional" : bond.productType} • Amount:{" "}
                              {formatCompactCurrency(
                                toNumberSafe(bond.issueAmount)
                              )}
                            </p>
                            {bond.isin && (
                              <p className="text-xs text-gray-400 mt-1">
                                ISIN: {bond.isin}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {getStatusBadge(bond.status)}
                            <p className="text-xs text-gray-500 mt-1">
                              {bond.submittedAt
                                ? `Submitted: ${formatDate(bond.submittedAt)}`
                                : bond.createdAt
                                ? `Created: ${formatDate(bond.createdAt)}`
                                : ""}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-500">
                        <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-lg font-medium mb-2">
                          No bonds created yet
                        </p>
                        <p className="text-sm mb-4">
                          Create your first bond to get started with issuance
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom create bond button removed per requirement */}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-medium ">Quick Actions</h2>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleCreateBond}
                    className="w-full h-24 flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
                  >
                    <Plus
                      className="w-6 h-6 mb-2"
                      style={{ color: "var(--color-primary)" }}
                    />
                    Create New Bond
                  </button>
                  <button
                    onClick={() => navigate("/issuer/announcements")}
                    className="w-full h-24 flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
                  >
                    <AlertCircle
                      className="w-6 h-6 mb-2"
                      style={{ color: "var(--color-primary)" }}
                    />
                    Make Announcement
                  </button>
                  <button
                    onClick={() => navigate("/issuer/roles")}
                    className="w-full h-24 flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
                  >
                    <Shield
                      className="w-6 h-6 mb-2"
                      style={{ color: "var(--color-primary)" }}
                    />
                    Manage Sub-Roles
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Activity section removed */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssuerDashboard;
